/**
 * DeliveryJobs Page
 * Mobile-optimized jobs list for Delivery users
 * Shows only assigned jobs for the selected date
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Briefcase, AlertCircle } from 'lucide-react';
import { AppShell } from '../../components/layout/AppShell';
import { useGetDeliveryJobsQuery, useGetUserProfileQuery } from '../../store/slices/apiSlice';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { EmptyState } from '../../components/common/EmptyState';
import { MobileJobCard } from './components/MobileJobCard';
import { DateFilter } from './components/DateFilter';

export const DeliveryJobs: React.FC = () => {
  const navigate = useNavigate();
  
  // Get current user profile to get the user ID for filtering
  const { data: userProfile } = useGetUserProfileQuery();
  
  // State
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoaded, setIsLoaded] = useState(true);
  
  // Get delivery user ID from profile (user.id is the Django User ID)
  const deliveryUserId = userProfile?.user?.id?.toString();
  
  // Fetch jobs - filtered by assigned delivery user and selected date
  const { data: jobsData, isLoading, isError, error } = useGetDeliveryJobsQuery(
    {
      potential_delivery_date__date: selectedDate,
      delivery_user: deliveryUserId,
      ordering: 'potential_delivery_date,-created_at',
    },
    { skip: !isLoaded || !selectedDate || !deliveryUserId }
  );
  
  const allJobs = jobsData?.results || [];
  
  // Filter jobs by search query
  const filteredJobs = useMemo(() => {
    if (!searchQuery.trim()) return allJobs;
    
    const query = searchQuery.toLowerCase();
    return allJobs.filter((job: any) => {
      const postcode = job.delivery_postcode?.toLowerCase() || '';
      const customerName = (job.customer_name || '').toLowerCase();
      const orderNumber = job.website_job_number?.toLowerCase() || '';
      
      return postcode.includes(query) || 
             customerName.includes(query) || 
             orderNumber.includes(query);
    });
  }, [allJobs, searchQuery]);
  
  // Stats
  const jobCount = filteredJobs.length;
  const totalItems = filteredJobs.reduce((sum: number, job: any) => sum + (job.total_items || 0), 0);
  
  // Handlers
  const handleViewJob = (jobId: string) => {
    navigate(`/jobs/${jobId}`);
  };
  
  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    setIsLoaded(true);
  };

  return (
    <AppShell stickyHeader={false}>
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-2xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
            {/* Title Row */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                  <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg sm:text-xl font-bold text-gray-900">My Jobs</h1>
                  <p className="text-xs sm:text-sm text-gray-500">
                    {jobCount} job{jobCount !== 1 ? 's' : ''} • {totalItems} item{totalItems !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Date Filter */}
            <div className="mb-3">
              <DateFilter 
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
              />
            </div>
            
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search postcode, customer, order #"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="max-w-2xl mx-auto px-3 sm:px-4 py-4">
          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12">
              <LoadingSpinner size="lg" />
              <p className="mt-4 text-gray-600">Loading your jobs...</p>
            </div>
          )}
          
          {/* Error State */}
          {isError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-red-900">Failed to load jobs</h3>
                  <p className="text-sm text-red-700 mt-1">
                    {(error as any)?.data?.detail || 'Please try again later.'}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Empty State */}
          {!isLoading && !isError && filteredJobs.length === 0 && (
            <EmptyState
              title={searchQuery ? 'No jobs found' : 'No jobs for this date'}
              description={
                searchQuery 
                  ? `No jobs match "${searchQuery}". Try a different search.`
                  : `You have no assigned jobs for ${new Date(selectedDate).toLocaleDateString('en-GB')}.`
              }
              icon={<Briefcase className="w-12 h-12 text-gray-400" />}
            />
          )}
          
          {/* Jobs List */}
          {!isLoading && !isError && filteredJobs.length > 0 && (
            <div className="space-y-4">
              {filteredJobs.map((job: any) => (
                <MobileJobCard
                  key={job.id}
                  job={{
                    id: job.id,
                    website_job_number: job.website_job_number,
                    reference: job.reference,
                    delivery_postcode: job.delivery_postcode,
                    customer_name: job.customer_name,
                    customer_first_name: job.customer_first_name,
                    customer_last_name: job.customer_last_name,
                    customer_mobile_number: job.customer_mobile_number,
                    delivery_address_line1: job.delivery_address_line1,
                    delivery_address_line2: job.delivery_address_line2,
                    total_items: job.total_items,
                    potential_delivery_date: job.potential_delivery_date,
                    delivery_window_start: job.delivery_window_start,
                    delivery_window_end: job.delivery_window_end,
                    delivery_status: job.delivery_status,
                    delivery_result: job.delivery_result,
                    delivery_notes: job.delivery_notes,
                  }}
                  onView={handleViewJob}
                />
              ))}
            </div>
          )}
        </div>
        
        {/* Bottom Spacer */}
        <div className="h-6 sm:h-8" />
      </div>
    </AppShell>
  );
};

DeliveryJobs.displayName = 'DeliveryJobs';
